import { Calendar, Users } from "lucide-react";
import StatusBadge from "../Program/StatusBadge";
import HighlightBadge from "../Progress/HighlightBadge";
import { computeHighlight, formatDateShort } from "../../utils/progressUtils";

function pic(sub, assignments, userById) {
  const a = assignments[sub.id];
  if (!a) return null;
  const ketua = a.ketuaId ? userById[a.ketuaId] : null;
  const anggotaCount = Array.isArray(a.anggotaIds) ? a.anggotaIds.length : 0;
  return { ketua, anggotaCount };
}

function daysLeft(iso) {
  if (!iso) return null;
  const target = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

export default function DeadlineTable({ subs, assignments, userById }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const in30 = new Date(today);
  in30.setDate(today.getDate() + 30);

  const rows = subs
    .filter((s) => {
      if (s.status === "SELESAI") return false;
      if (!s.targetSelesai) return false;
      const t = new Date(s.targetSelesai);
      return t >= today && t <= in30;
    })
    .sort((a, b) => new Date(a.targetSelesai) - new Date(b.targetSelesai));

  return (
    <div
      data-testid="chart-deadline-table"
      className="rounded-2xl border border-amber-100/60 p-4 shadow-sm print:shadow-none relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #fffbeb 0%, #ffffff 55%)" }}
    >
      {/* Colored header band */}
      <div
        className="-mx-4 -mt-4 px-4 py-4 mb-4 rounded-t-2xl"
        style={{ background: "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)" }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/20">
              <Calendar className="h-5 w-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <h3 className="font-display text-base font-bold text-white leading-tight">
                Deadline 30 Hari ke Depan
              </h3>
              <p className="text-[11px] text-amber-100 mt-0.5">
                Sub-kegiatan yang harus selesai dalam 30 hari ke depan.
              </p>
            </div>
          </div>
          <span className="text-[11px] font-semibold text-white bg-white/20 px-2.5 py-1 rounded-full">
            {rows.length} kegiatan
          </span>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="mt-4 flex flex-col items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center">
          <Calendar className="h-5 w-5 text-slate-400" strokeWidth={1.8} />
          <div className="mt-2 text-[12.5px] font-semibold text-slate-700">
            Tidak ada deadline dalam 30 hari
          </div>
          <div className="text-[11.5px] text-slate-500">
            Semua sub-kegiatan di luar jendela 30 hari ke depan.
          </div>
        </div>
      ) : (
        <div className="mt-3 overflow-hidden rounded-md border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-[12px]">
            <thead className="bg-amber-50/60 text-[10px] uppercase tracking-[0.1em] text-amber-800/70">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Nama Kegiatan</th>
                <th className="px-3 py-2 text-left font-semibold">Deadline</th>
                <th className="px-3 py-2 text-left font-semibold">Status</th>
                <th className="px-3 py-2 text-left font-semibold">PIC</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {rows.map((s) => {
                const p = pic(s, assignments, userById);
                const dl = daysLeft(s.targetSelesai);
                const highlight = computeHighlight(s);
                return (
                  <tr key={s.id} data-testid={`deadline-row-${s.id}`} className="hover:bg-amber-50/30">
                    <td className="max-w-[320px] px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10.5px] text-slate-500">
                          {s.programKode}
                        </span>
                        <span className="truncate font-medium text-slate-900" title={s.nama}>
                          {s.nama}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <div className="flex flex-col leading-tight">
                        <span className="text-slate-800">{formatDateShort(s.targetSelesai)}</span>
                        <span className="mt-0.5">
                          <HighlightBadge highlight={highlight} />
                          {!highlight.label && dl !== null ? (
                            <span className="text-[10.5px] text-slate-500">
                              {dl} hari lagi
                            </span>
                          ) : null}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge status={s.status} />
                    </td>
                    <td className="px-3 py-2">
                      {p ? (
                        <div className="flex flex-col leading-tight">
                          <span className="text-slate-800">
                            {p.ketua?.name || (
                              <span className="italic text-slate-400">belum ditugaskan</span>
                            )}
                          </span>
                          <span className="mt-0.5 inline-flex items-center gap-1 text-[10.5px] text-slate-500">
                            <Users className="h-3 w-3" strokeWidth={1.8} />
                            {p.anggotaCount} anggota
                          </span>
                        </div>
                      ) : (
                        <span className="italic text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
